// ═══════════════════════════════════════════════════════════════════════════════
// Gate: authenticated + paired + consented — then render main app shell + Outlet
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { USER_ROLE } from '@fdh/shared';
import type { UserProfile } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslationSafe } from '../../contexts/LanguageContext';
import { reconcilePairedUserProfile } from '../../lib/reconcileUserPairing';
import { MainAppShell } from './MainAppShell';

const isPaired = (pairedAt: string | undefined): boolean =>
  Boolean(pairedAt && pairedAt.trim() !== '');

type PairingSyncPhase = 'idle' | 'syncing' | 'done';
type VersionCheckPhase = 'idle' | 'checking' | 'ready' | 'mismatch';

const VERSION_CHECK_PATHS = new Set(['/home', '/dashboard']);
const VERSION_SLOW_HINT_DELAY_MS = 8_000;
const VERSION_FORCE_RELOAD_DELAY_MS = 13_000;
const VERSION_MODAL_EVENTS_KEY = 'fdh-version-mismatch-modal-events';
const VERSION_MODAL_WINDOW_MS = 3_600_000;
const VERSION_MODAL_MAX_PER_HOUR = 4;

const readVersionModalEventTimestamps = (): number[] => {
  try {
    const raw = sessionStorage.getItem(VERSION_MODAL_EVENTS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((x): x is number => typeof x === 'number');
  } catch {
    return [];
  }
};

const pruneVersionModalEvents = (): number[] => {
  const now = Date.now();
  const kept = readVersionModalEventTimestamps().filter((t) => now - t < VERSION_MODAL_WINDOW_MS);
  sessionStorage.setItem(VERSION_MODAL_EVENTS_KEY, JSON.stringify(kept));
  return kept;
};

const recordVersionModalShown = (): void => {
  const now = Date.now();
  const next = [...pruneVersionModalEvents(), now];
  sessionStorage.setItem(VERSION_MODAL_EVENTS_KEY, JSON.stringify(next));
};

export const MainAppGate = () => {
  const t = useTranslationSafe();
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading, user, refreshUser, updateUser } = useAuth();
  const [pairingSyncPhase, setPairingSyncPhase] = useState<PairingSyncPhase>('idle');
  const [reconciledProfile, setReconciledProfile] = useState<UserProfile | null>(null);
  const [versionCheckPhase, setVersionCheckPhase] = useState<VersionCheckPhase>('idle');
  const [showSlowUpdateHint, setShowSlowUpdateHint] = useState(false);
  const [versionMismatchLabels, setVersionMismatchLabels] = useState<{
    frontend: string;
    backend: string;
  } | null>(null);

  const shouldCheckVersion = useMemo(
    () => VERSION_CHECK_PATHS.has(pathname),
    [pathname],
  );

  useEffect(() => {
    setPairingSyncPhase('idle');
    setReconciledProfile(null);
  }, [user?.id]);

  useEffect(() => {
    if (isLoading || !user) {
      return;
    }
    if (isPaired(user.pairedAt)) {
      setReconciledProfile(null);
      return;
    }
    if (pairingSyncPhase !== 'idle') {
      return;
    }
    setPairingSyncPhase('syncing');
    void reconcilePairedUserProfile(refreshUser, updateUser, user)
      .then((profile) => {
        setReconciledProfile(profile);
      })
      .catch(() => {
        setReconciledProfile(null);
      })
      .finally(() => {
        setPairingSyncPhase('done');
      });
  }, [isLoading, user, user?.id, user?.pairedAt, pairingSyncPhase, refreshUser, updateUser]);

  useEffect(() => {
    if (versionCheckPhase !== 'mismatch') {
      setShowSlowUpdateHint(false);
      return;
    }

    const slowHintTimer = window.setTimeout(() => {
      setShowSlowUpdateHint(true);
    }, VERSION_SLOW_HINT_DELAY_MS);
    const forceReloadTimer = window.setTimeout(() => {
      window.location.reload();
    }, VERSION_FORCE_RELOAD_DELAY_MS);

    return () => {
      window.clearTimeout(slowHintTimer);
      window.clearTimeout(forceReloadTimer);
    };
  }, [versionCheckPhase]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user || !shouldCheckVersion) {
      return;
    }

    let isCancelled = false;
    setVersionCheckPhase('checking');

    void (async () => {
      try {
        const response = await fetch('/api/health', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) {
          if (!isCancelled) {
            setVersionMismatchLabels(null);
            setVersionCheckPhase('ready');
          }
          return;
        }
        const data = (await response.json()) as { version?: unknown };
        const backendVersion =
          typeof data.version === 'string' ? data.version.trim() : '';
        const frontendVersion = import.meta.env.VITE_APP_VERSION.trim();
        if (backendVersion === '' || backendVersion === frontendVersion) {
          if (!isCancelled) {
            setVersionMismatchLabels(null);
            setVersionCheckPhase('ready');
          }
          return;
        }

        if (isCancelled) {
          return;
        }

        const recentModalEvents = pruneVersionModalEvents();
        if (recentModalEvents.length >= VERSION_MODAL_MAX_PER_HOUR) {
          if (!isCancelled) {
            setVersionMismatchLabels(null);
            setVersionCheckPhase('ready');
          }
          return;
        }

        if (!isCancelled) {
          recordVersionModalShown();
          setVersionMismatchLabels({ frontend: frontendVersion, backend: backendVersion });
          setVersionCheckPhase('mismatch');
        }
      } catch {
        if (!isCancelled) {
          setVersionMismatchLabels(null);
          setVersionCheckPhase('ready');
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [isLoading, isAuthenticated, user, shouldCheckVersion]);

  const userForGate: UserProfile | null = (() => {
    if (!user) {
      return null;
    }
    if (reconciledProfile && isPaired(reconciledProfile.pairedAt)) {
      return reconciledProfile;
    }
    return user;
  })();

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#f7f8fb] dark:bg-gray-950"
        role="status"
        aria-label={t('protected.loadingAria')}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (versionCheckPhase === 'mismatch') {
    const versionLine =
      versionMismatchLabels != null
        ? t('app.autoUpdatingVersionLine', {
            currentVer: versionMismatchLabels.frontend,
            newVer: versionMismatchLabels.backend,
          })
        : '';
    return (
      <div
        className="fixed inset-0 z-[220] flex items-center justify-center bg-black/70 p-6"
        role="dialog"
        aria-modal="true"
        aria-label={t('app.autoUpdatingTitle')}
        aria-describedby={versionLine !== '' ? 'fdh-version-mismatch-version-line' : undefined}
      >
        <div className="relative w-full max-w-sm rounded-2xl bg-white px-6 pb-12 pt-7 text-center shadow-2xl dark:bg-gray-900">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('app.autoUpdatingTitle')}
          </p>
          {showSlowUpdateHint ? (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t('app.autoUpdatingSlowHint')}
            </p>
          ) : null}
          {versionLine !== '' ? (
            <p
              id="fdh-version-mismatch-version-line"
              className="absolute bottom-3 left-4 max-w-[calc(100%-2rem)] text-left text-[11px] leading-snug text-gray-500 dark:text-gray-400"
            >
              {versionLine}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (!userForGate) {
    return <Navigate to="/" replace />;
  }

  if (!isPaired(userForGate.pairedAt)) {
    if (pairingSyncPhase !== 'done') {
      return (
        <div
          className="flex min-h-screen items-center justify-center bg-[#f7f8fb] dark:bg-gray-950"
          role="status"
          aria-label={t('protected.loadingAria')}
        >
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      );
    }
    if (!userForGate.emailVerified) {
      const next =
        userForGate.role === USER_ROLE.EMPLOYER ? '/pairing/generate-code' : '/pairing/join';
      return <Navigate to={next} replace />;
    }
    if (!userForGate.consentedAt || userForGate.consentedAt.trim() === '') {
      return <Navigate to="/consent" replace />;
    }
    if (userForGate.role !== USER_ROLE.EMPLOYER) {
      return <Navigate to="/pairing/join" replace />;
    }
  }

  if (!userForGate.emailVerified) {
    return <Navigate to="/pairing/status" replace />;
  }

  if (!userForGate.consentedAt || userForGate.consentedAt.trim() === '') {
    return <Navigate to="/consent" replace />;
  }

  return (
    <MainAppShell>
      <Outlet />
    </MainAppShell>
  );
};
